/**
 * Shared display name utilities that respect user privacy settings
 *
 * When a user enables "Hide Real Name" (show_username_only = true),
 * their username should be displayed instead of their real name.
 *
 * This utility provides centralized functions for getting display names
 * that work across both web and mobile platforms.
 */

export interface UserPrivacyData {
  first_name?: string | null;
  last_name?: string | null;
  username?: string | null;
  show_username_only?: boolean | null;
  company_name?: string | null; // For clients
}

/**
 * Get the display name for a user, respecting their privacy settings.
 *
 * Priority:
 * 1. If show_username_only && username exists -> return username
 * 2. If company_name exists (client) -> return company_name
 * 3. If first_name/last_name exists -> return "first_name last_name"
 * 4. Fallback to provided fallback or 'Unknown User'
 */
export function getDisplayName(
  user: UserPrivacyData | null | undefined,
  fallback = 'Unknown User'
): string {
  if (!user) return fallback;

  // Check privacy setting first
  if (user.show_username_only && user.username) {
    return user.username;
  }

  // For clients, prefer company name
  if (user.company_name) {
    return user.company_name;
  }

  // Build full name from first/last
  const firstName = user.first_name || '';
  const lastName = user.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || fallback;
}

/**
 * Get initials for avatar fallback, respecting privacy settings.
 *
 * Priority:
 * 1. If show_username_only && username exists -> first 2 chars of username
 * 2. If company_name exists -> first 2 chars of company name
 * 3. If first_name/last_name exists -> first char of each
 * 4. Fallback to '??'
 */
export function getInitials(
  user: UserPrivacyData | null | undefined,
  fallback = '??'
): string {
  if (!user) return fallback;

  // Check privacy setting first
  if (user.show_username_only && user.username) {
    return user.username.substring(0, 2).toUpperCase();
  }

  // For clients, use company name initials
  if (user.company_name) {
    const words = user.company_name.split(' ').filter(Boolean);
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return user.company_name.substring(0, 2).toUpperCase();
  }

  // Use first/last name initials
  const firstInitial = user.first_name?.[0] || '';
  const lastInitial = user.last_name?.[0] || '';
  const initials = `${firstInitial}${lastInitial}`.toUpperCase();

  return initials || fallback;
}

/**
 * Format user data from API response to include display_name.
 * Use this when processing API responses that include user data.
 */
export function withDisplayName<T extends UserPrivacyData>(
  user: T | null | undefined,
  fallback = 'Unknown User'
): (T & { display_name: string; initials: string }) | null {
  if (!user) return null;

  return {
    ...user,
    display_name: getDisplayName(user, fallback),
    initials: getInitials(user),
  };
}

/**
 * Batch process users to add display names.
 * Useful when processing lists of users from API responses.
 */
export function withDisplayNames<T extends UserPrivacyData>(
  users: T[] | null | undefined,
  fallback = 'Unknown User'
): Array<T & { display_name: string; initials: string }> {
  if (!users) return [];

  return users.map(user => ({
    ...user,
    display_name: getDisplayName(user, fallback),
    initials: getInitials(user),
  }));
}
